import statuscode_es from "../translations/ES/statuscode.json";
import statuscode_en from "../translations/EN/statuscode.json";

const lang = localStorage.getItem("lang") || "es";
const language = lang === "es" ? statuscode_es : statuscode_en;

const CONTEXT_KEYS = {
  // En estos casos se está dando un mensaje específico
  // ya que uno genérico no mostraría bien el error
  auth_recovery: {
    401: "invalid_credentials_or_code",
  },
  advisor_review: {
    403: "advisor_review_forbidden",
  },
  ai: {
    403: "ai_forbidden",
  },
  assignments: {
    403: "assignments_forbidden",
  },
  attachments: {
    403: "attachments_forbidden",
  },
  catalogues: {
    403: "catalogues_forbidden",
  },
  cognito: {
    403: "cognito_forbidden",
  },
  config: {
    403: "config_forbidden",
  },
  dashboard: {
    403: "dashboard_forbidden",
  },
  eam: {
    403: "eam_forbidden",
  },
  exports: {
    403: "exports_forbidden",
  },
  forms: {
    403: "forms_forbidden",
  },
  investigation: {
    403: "investigation_forbidden",
  },
  landing: {
    403: "landing_forbidden",
  },
  messages: {
    403: "messages_forbidden",
  },
  notifications: {
    403: "notifications_forbidden",
  },
  reports: {
    403: "reports_forbidden",
  },
  users: {
    403: "users_forbidden",
  },
  utils: {
    403: "utils_forbidden",
  },
};

// Mapeo de señales del backend a claves i18n. Solo se usan en 400/409/422
const BACKEND_PATTERNS = [
  { match: /at least one logo|al menos un logotipo/i, key: "at_least_one_logo_required" },
  { match: /plan is not valid|plan.+no.+v[aá]lido/i, key: "plan_is_not_valid" },
  { match: /resolution.+verified facts|resoluci[oó]n.+hecho verificado/i, key: "resolution_missing_verified_facts" },
  { match: /verify.+general configuration|verifica.+configuraci[oó]n general/i, key: "verify_general_configuration_completed" },
  { match: /already taken|ya est[aá] en uso/i, key: "duplicate_resource_error" },
  { match: /password must be at least 8/i, key: "segure_account_strong_password" },
  { match: /phone_number is required/i, key: "enter_valid_phone_number" },
];

// Fallback por nombre de campo DRF (forma { campo: ["msg"] })
const DRF_FIELD_TO_KEY = {
  email: "duplicate_resource_error",
  username: "duplicate_resource_error",
  phone_number: "enter_valid_phone_number",
};

const collectStrings = (data) => {
  if (!data) return [];
  if (typeof data === "string") return [data];
  if (typeof data !== "object") return [];
  const out = [];
  if (typeof data.error === "string") out.push(data.error);
  if (typeof data.message === "string") out.push(data.message);
  for (const value of Object.values(data)) {
    if (Array.isArray(value)) {
      for (const v of value) if (typeof v === "string") out.push(v);
    }
  }
  return out;
};

const detectBackendKey = (error) => {
  const data = error?.response?.data;
  const candidates = collectStrings(data);
  for (const candidate of candidates) {
    for (const { match, key } of BACKEND_PATTERNS) {
      if (match.test(candidate) && language[key]) return key;
    }
  }
  if (data && typeof data === "object" && !Array.isArray(data)) {
    for (const field of Object.keys(data)) {
      if (Array.isArray(data[field]) && DRF_FIELD_TO_KEY[field] && language[DRF_FIELD_TO_KEY[field]]) {
        return DRF_FIELD_TO_KEY[field];
      }
    }
  }
  return null;
};

export const getErrorKey = (error, context) => {
  if (error?.code === "ECONNABORTED" || error?.code === "ERR_NETWORK" || (error && !error.response)) {
    return "timeout_error";
  }

  const status = error?.response?.status;

  if (status === 400 || status === 422) {
    const backendKey = detectBackendKey(error);
    if (backendKey) return backendKey;
    return "invalid_data_error";
  }

  if (status === 401) {
    const contextMap = CONTEXT_KEYS[context];
    if (contextMap && contextMap[status] && language[contextMap[status]]) {
      return contextMap[status];
    }
    return "session_expired";
  }

  if (status === 403) {
    const contextMap = CONTEXT_KEYS[context];
    if (contextMap && contextMap[status] && language[contextMap[status]]) {
      return contextMap[status];
    }
    return "forbidden_error";
  }

  if (status === 404) {
    return "resource_not_found_error";
  }

  if (status === 409) {
    const backendKey = detectBackendKey(error);
    if (backendKey) return backendKey;
    return "duplicate_resource_error";
  }

  if (status === 413) {
    return "file_too_large_error";
  }

  if (status === 429) {
    return "too_many_requests_error";
  }

  if (status >= 500 && status < 600) {
    return "server_error";
  }

  return "generic_error";
};

export const getErrorMessage = (error, context) => language[getErrorKey(error, context)];
